import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  graphql, useRefetchableFragment, useRelayEnvironment,
} from 'react-relay';

import DashboardControl from 'src/components/Dashboard/DashboardControl';
import { WARNING_TIMEOUT, showTradeRuleMsg } from 'src/components/Dashboard/DashboardControl/helpers/common';
import LoadingOverlay from 'src/components/Dashboard/LoadingOverlay/index';
import { buildChartViewKey, buildStateURLParams, updateEntityShowState } from 'src/components/Dashboard/helpers/common';
import subHeading from 'src/components/Portfolio/helpers/common';
import { APIConfig } from 'src/config';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import FlashesStore from 'src/stores/FlashesStore';
import {
  MODAL_CLOSE_DELAY, P1D,
  PT30M, SOURCE, SOURCE_TRADES,
  TIME_ZONE_SYSTEM, UNIT, UNIT_CURRENCY, UNIT_ENERGY,
  VIEW_SELECTOR,
} from 'src/util/constants';
import BrowserProtocol from 'src/util/history';
import { getDefaultLocale } from 'src/util/i18n/handler';
import logger from 'src/util/logger';
import { dateRangeToTimeRange } from 'src/util/time';
import buildLocalizedDateRangeLabel from 'src/util/time/dateRange';

import PortfolioShowTradeEnergy from './PortfolioShowTradeEnergy';
import { buildMainData } from './data/mainDataBuilder';
import {
  fetchPortfolioDataInChunks,
  mergePortfolioDataChunks,
} from './helpers/dataChunker';
import chartViewSelector from './helpers/portfolioShow';
// Portfolio fragment for static data, not varying over time
const PortfolioShowFragment = graphql`
  fragment PortfolioShow on Query
  @refetchable(queryName: "PortfolioShowRefetchQuery")
  @argumentDefinitions(
    uuid: { type: "ID!" }
  ) {
    portfolio(uuid: $uuid) {
      uuid
      externalIdentifier
      active { start finish }
      title
      propertyMembers(first: 500) {
        edges {
          node {
            uuid
            externalIdentifier
            active { start finish }
            property {
              uuid
              externalIdentifier
              uuid
              title
              publicHolidayRegion
            }
          }
        }
      }
      userMembers(first: 500) {
        edges {
          node {
            uuid
            externalIdentifier
            active { start finish }
            role
            user {
              uuid
              externalIdentifier
              email
              givenName
              familyName
              active { start finish }
            }
          }
        }
      }
    }
  }
`;

// Define a direct query for our chunked fetching, but it will directly use our fields
// rather than referencing the fragment to avoid automatic data fetching
const PortfolioChunkQuery = graphql`
  query PortfolioShowChunkQuery(
    $uuid: ID!,
    $start: Timestamp!,
    $finish: Timestamp!,
    $timeZone: String!,
    $aggregation: String!,
  ) {
    portfolio(uuid: $uuid) {
      uuid
      externalIdentifier
      active { start finish }
      title
      meters(first:500) {
        edges {
          node {
            uuid
            identifier
            externalIdentifier
            active { start finish }
            title
            tradePointId
            primaryBillingPoint {
              identifier
            }
            property {
              uuid
            }
            rules(first:999999999, start: $start, finish: $finish) {
              edges {
                node {
                  uuid
                  tradeType
                  buyer {
                    userId
                    user {
                      uuid
                      email
                      givenName
                      familyName
                    }
                    tradePoint {
                      uuid
                      type
                      meter {
                        uuid
                        identifier
                        title
                        property {
                          uuid
                          title
                        }
                      }
                    }
                  }
                  seller {
                    userId
                    user {
                      uuid
                      email
                      givenName
                      familyName
                    }
                    tradePoint {
                      uuid
                      type
                      meter {
                        uuid
                        identifier
                        title
                        property {
                          uuid
                          title
                        }
                      }
                    }
                  }
                  state
                  clauses {
                    edges {
                      node {
                        price
                        ignorePublicHolidays
                        monthsOfYear
                        daysOfWeek
                        timesOfDay {
                          start { hours minutes }
                          finish { hours minutes }
                        }
                      }
                    }
                  }
                  proposedAt
                  proposedBy {
                    uuid
                    email
                    givenName
                    familyName
                  }
                  acceptedAt
                  acceptedBy {
                    uuid
                    email
                    givenName
                    familyName
                  }
                  start
                  finish
                }
              }
            }
            dataConsumed: calculatedData(start: $start, finish: $finish, aggregation: $aggregation, timeZone: $timeZone, metric: "elec_energy_consumed") {
              aggregation
              metric {
                identifier
              }
              timeZone
              timeRange {
                start
                finish
              }
              data {
                timestamp
                value
                flags {
                  identifier
                  description
                  Valid
                }
              }
            }
            dataGenerated: calculatedData(start: $start, finish: $finish, aggregation: $aggregation, timeZone: $timeZone, metric: "elec_energy_generated") {
              aggregation
              metric {
                identifier
              }
              timeZone
              timeRange {
                start
                finish
              }
              data {
                timestamp
                value
                flags {
                  identifier
                  description
                  Valid
                }
              }
            }
          }
        }
      }
      tradeSetSummary(start: $start, finish: $finish, timeZone: $timeZone, aggregation: $aggregation, groups: [SUMMARY_GROUP_TRADE_POINT, SUMMARY_GROUP_TRADE_RULE, SUMMARY_GROUP_DIRECTION, SUMMARY_GROUP_TRADE_TYPE]) {
        key {
          tradePointId
          ruleId
          direction
          type
        }
        data {
          range { start finish }
          buyerTradePointIds
          sellerTradePointIds
          directions
          types
          value
          volume
          averagePrice
          tradeCount
          intervalCount
        }
      }
    }
  }
`;

/**
 * Description
 * @param {any} props
 * @returns {React.ReactComponentElement} - PortfolioShow component
 */
function PortfolioShow(props) {
  // Get environment directly - don't use any Relay hooks that might trigger data fetching
  const environment = useRelayEnvironment();
  const { queryParams = {} } = props;
  const {
    timezone: queryTimezone,
    aggregateBy: queryAggregateBy,
    groupBy: queryGroupBy,
    xAxisDomain: queryXAxisDomain,
    source: querySource,
    unit: queryUnit,
  } = queryParams;
  const [initialData] = useRefetchableFragment(PortfolioShowFragment, props);
  const initialView = {
    aggregateBy: queryAggregateBy,
    groupBy: queryGroupBy,
    xAxisDomain: queryXAxisDomain,
  };

  // Get portfolio ID and time range from props
  const portfolioId = props?.match?.params?.uuid;
  const matchLocation = props?.match?.location || {};
  const searchParams = new URLSearchParams(matchLocation.search || window.location.search);

  // Get timezone information
  const portfolioTimezone = APIConfig().DEFAULT_TIMEZONE || TIME_ZONE_SYSTEM;
  const timezone = queryTimezone || portfolioTimezone;

  // Setup datespan from URL or use defaults
  const startParam = searchParams.get('start');
  const finishParam = searchParams.get('finish');
  const defaultStart = DateTime.now().minus({ days: 6 }).setZone(timezone).startOf('day');
  const defaultFinish = DateTime.now().setZone(timezone).endOf('day');

  // I18n messages for loading overlay
  const intl = useIntl();
  const preparingDataAndRendering = intl.formatMessage({ id: 'portfolio.portfolio_show.preparing_data_and_rendering.overlay.body.message', defaultMessage: 'Preparing data and rendering.' });
  const preparingToApplyNewDateRange = intl.formatMessage({ id: 'portfolio.portfolio_show.preparing_to_apply_new_date_range.overlay.body.message', defaultMessage: 'Preparing to apply new date range.' });
  const newDateRangeApplied = intl.formatMessage({ id: 'portfolio.portfolio_show.new_date_range_applied.overlay.body.message', defaultMessage: 'New date range applied.' });

  // State to manage chunks
  const [, setDataChunks] = useState([]); // Using empty array but keeping setter for compatibility
  const [loadingChunks, setLoadingChunks] = useState(false);
  const [chunksLoaded, setChunksLoaded] = useState(0);
  const [chunksTotal, setTotalChunks] = useState(0);
  const [combinedData, setCombinedData] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isfullyLoaded, setIsFullyLoaded] = useState(false);
  const [progressBarLabel, setProgressBarLabel] = useState(preparingToApplyNewDateRange);

  // Parse selected keys from URL parameters (comma-separated format)
  // Only stores selectedKeys for the current view
  const cardSelectedKeysParam = searchParams.get('cardSelectedKeys');
  const initialSelectedKeys = useMemo(() => {
    if (!cardSelectedKeysParam) {
      return {};
    }

    // Build the chart view key for the current view
    const chartViewKey = buildChartViewKey(initialView, querySource);

    if (!chartViewKey) {
      return {};
    }

    // Parse comma-separated keys
    const keysArray = cardSelectedKeysParam.split(',').filter((key) => key.trim() !== '');

    // Return selectedKeys object with current view's keys
    return keysArray.length > 0 ? { [chartViewKey]: keysArray } : {};
  }, [cardSelectedKeysParam, initialView, querySource]);

  const [selectedKeys, setSelectedKeys] = useState(initialSelectedKeys);

  // States for the component
  const isPending = false; // Since we're not using useTransition anymore
  const [unit, setUnit] = useState(queryUnit);
  const [source, setSource] = useState(querySource);
  const [tradeBtnClicks, setTradeBtnClicks] = useState(0);
  const [viewSelectorOpen, setViewSelectorOpen] = useState(false);
  const [downloadSelectorOpen, setDownloadSelectorOpen] = useState(false);
  const [chartView, setChartView] = useState(initialView);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const firstTimeRender = useRef(true);

  // Process initial dates from URL
  const getInitialDatespan = () => {
    let start = defaultStart;
    let finish = defaultFinish;

    // Parse start date from URL if present
    if (startParam) {
      const parsedStart = DateTime.fromISO(startParam, { zone: timezone });
      if (parsedStart.isValid) {
        start = parsedStart.startOf('day');
      } else {
        logger.warn('Invalid start date parameter:', startParam);
      }
    }

    // Parse finish date from URL if present
    if (finishParam) {
      const parsedFinish = DateTime.fromISO(finishParam, { zone: timezone });
      if (parsedFinish.isValid) {
        finish = parsedFinish.endOf('day');
      } else {
        logger.warn('Invalid finish date parameter:', finishParam);
      }
    }

    logger.log('Setting initial datespan:', start.toISO(), 'to', finish.toISO());
    return { start, finish };
  };

  // Initial datespan state - ensure correct parsing of date parameters
  const [datespan, setDatespan] = useState(getInitialDatespan);

  // State for storing aggregation and chunk size
  const [aggregation, setAggregation] = useState(PT30M);
  const [chunkSizeHours, setChunkSizeHours] = useState(4);

  // Track the date range to detect actual changes
  const lastFetchedRange = useRef(null);

  // This is a flag to prevent recursive calls from URL updates
  const lastUpdatedURL = useRef(null);

  // This tracks if we're currently fetching data to prevent parallel fetches
  const isFetchingData = useRef(false);

  const { match } = props || {};

  // Update the URL with the new time range
  const updateURLWithTimeRange = (startTime, finishTime) => {
    try {
      // Check if we have props and match
      if (!match || !match.location) {
        logger.warn('Missing match or location for URL update, skipping');
        return;
      }

      // Using already destructured props
      const { location } = match;
      const { pathname } = location || { pathname: window.location.pathname };

      // Make sure dates are in the correct timezone for URL display
      const startInTimezone = startTime.setZone(timezone);
      const finishInTimezone = finishTime.setZone(timezone);

      // Generate URL signature to check if it changed
      const urlSignature = `${startInTimezone.toISODate()}-${finishInTimezone.toISODate()}-${timezone}`;

      // Skip URL update if it's identical to the last one
      if (lastUpdatedURL.current === urlSignature) {
        logger.log('Skipping duplicate URL update:', urlSignature);
        return;
      }

      // Store the current URL signature
      lastUpdatedURL.current = urlSignature;

      logger.log(
        'Updating URL with time range:',
        startInTimezone.toISODate(),
        'to',
        finishInTimezone.toISODate(),
        '(timezone:',
        timezone,
        ')',
      );

      const state = buildStateURLParams(
        chartView,
        startTime,
        finishTime,
        timezone,
        source,
        unit,
        selectedKeys,
      );

      if (!state) {
        logger.warn('Failed to generate URL state, skipping update');
        return;
      }

      // Create the search parameter string
      const searchString = `?${Object.keys(state)
        .map((k) => `${k}=${encodeURIComponent(state[k])}`)
        .join('&')}`;

      // Compare with current URL search to avoid unnecessary history updates
      const currentSearch = window.location.search;
      if (currentSearch === searchString) {
        logger.log('URL search params already match desired state, skipping update');
        return;
      }

      // Only update if BrowserProtocol is available
      if (BrowserProtocol && typeof BrowserProtocol.navigate === 'function') {
        // Make sure to include the action parameter
        BrowserProtocol.navigate({
          action: 'REPLACE', // Explicitly set the action parameter
          pathname,
          search: searchString,
          hash: '',
          state,
        });
      } else {
        logger.warn('BrowserProtocol not available, using history.replaceState');
        // Fallback to using history API directly
        window.history.replaceState(
          state,
          '',
          `${pathname}${searchString}`,
        );
      }
    } catch (error) {
      logger.error('Error updating URL:', error);
    }
  };

  // Debug effect to track chunksLoaded state
  useEffect(() => {
    logger.log(`chunksLoaded changed: ${chunksLoaded}/${chunksTotal}, loadingChunks=${loadingChunks}`);
  }, [chunksLoaded, chunksTotal, loadingChunks]);

  // Unified effect to handle both initial data load and timespan updates
  useEffect(() => {
    // Extract the portfolio ID from multiple possible sources
    const uuidFromParams = props?.match?.params?.uuid;
    const searchStr = props?.match?.location?.search || window.location.search;
    const urlParamsUuid = new URLSearchParams(searchStr).get('uuid');
    const actualPortfolioId = portfolioId || uuidFromParams || urlParamsUuid;
    if (!actualPortfolioId) {
      logger.error('No portfolio ID found in URL parameters');
      FlashesStore.flash(FlashesStore.ERROR, 'Portfolio ID is missing from URL');
      return;
    }

    // Create a signature of the current datespan to compare
    const currentRange = `${datespan.start.toISO()}_${datespan.finish.toISO()}`;

    // Skip if this exact range was already fetched (prevents loops),
    // but always process on first render
    if (!firstTimeRender.current && lastFetchedRange.current === currentRange) {
      logger.log('Skipping duplicate data fetch for range:', currentRange);
      return;
    }

    // Record this range as being fetched
    lastFetchedRange.current = currentRange;

    // Calculate appropriate aggregation and chunk size based on date range
    const timespanDuration = datespan.finish.diff(datespan.start, 'days').days;
    let newAggregation = PT30M;
    // Using 2 hours due to size
    let newChunkSizeHours = 2;

    if (timespanDuration > 2) {
      newAggregation = P1D;
      newChunkSizeHours = 24;
    }

    // Update state with new values if they've changed
    if (newAggregation !== aggregation) {
      setAggregation(newAggregation);
    }

    if (newChunkSizeHours !== chunkSizeHours) {
      setChunkSizeHours(newChunkSizeHours);
    }

    // Log differently based on whether it's first render or an update
    if (firstTimeRender.current) {
      logger.log('Initial render with portfolio ID:', actualPortfolioId);
      logger.log(
        'Initial date range from URL:',
        datespan.start.toISO(),
        'to',
        datespan.finish.toISO(),
        '(timezone:',
        timezone,
        ')',
      );
      logger.log(`Using aggregation: ${newAggregation}, chunk size: ${newChunkSizeHours} hours`);
      // Mark as no longer first render after this execution
      firstTimeRender.current = false;
    } else {
      logger.log('Date range changed, refreshing data with:', datespan.start.toISO(), 'to', datespan.finish.toISO());
      logger.log(`Using aggregation: ${newAggregation}, chunk size: ${newChunkSizeHours} hours`);
    }

    // Fetch the data in chunks - the same process for both initial load and updates
    fetchPortfolioDataInChunks({
      portfolioId: actualPortfolioId,
      dateSpan: datespan,
      timezone,
      environment,
      query: PortfolioChunkQuery,
      initialData,
      isFetchingRef: isFetchingData,
      onLoadingChange: setLoadingChunks,
      onTotalChunksChange: setTotalChunks,
      aggregation: newAggregation, // Use the newly calculated aggregation
      chunkSizeHours: newChunkSizeHours, // Use the newly calculated chunk size
      onChunkLoaded: (increment, total) => {
        if (increment === 0) {
          setIsInitializing(false);
          setChunksLoaded(0);
        } else {
          setChunksLoaded((prev) => {
            const newValue = prev + increment;
            logger.log(`Updated chunks loaded: ${newValue}/${total}`);
            return newValue;
          });
        }
      },
      onDataReceived: (mergedData, validResults) => {
        logger.log('Received merged data with portfolio:', !!mergedData?.portfolio);
        setCombinedData(mergedData);
        setDataChunks(validResults || []);
        setIsFullyLoaded(true);
        setProgressBarLabel(preparingDataAndRendering);
      },
      onError: (error, message) => {
        logger.error('Error in data fetching:', error);
        FlashesStore.flash(FlashesStore.ERROR, message || 'Error fetching data');
      },
      onTimeRangeUpdated: updateURLWithTimeRange,
      mergeFunction: mergePortfolioDataChunks,
    });
  }, [datespan, portfolioId, environment, initialData, timezone, aggregation, chunkSizeHours]);

  const updateURLState = useCallback(() => {
    updateEntityShowState(datespan, match, chartView, timezone, source, unit, selectedKeys);
  }, [datespan, match, chartView, timezone, source, unit, selectedKeys]);

  useEffect(() => {
    if (!firstTimeRender.current) {
      updateURLState();
    }
  }, [updateURLState]);

  // Safely extract portfolio data
  const { portfolio } = combinedData || { portfolio: null };
  const { active, title, uuid } = portfolio || {
    active: { start: null, finish: null },
    title: 'Loading...',
    uuid: null,
  };

  const toggle = (dropdownType) => {
    if (dropdownType === VIEW_SELECTOR) {
      setViewSelectorOpen(!viewSelectorOpen);
    } else {
      setDownloadSelectorOpen(!downloadSelectorOpen);
    }
  };

  const controlSetStateFunc = (opts) => {
    const { group, value } = opts;

    let chartSource = source;
    let chartUnit = unit;
    let btnClicks = tradeBtnClicks;

    FlashesStore.reset();
    if (value === chartSource && chartSource === SOURCE_TRADES) {
      if (btnClicks === 0) {
        setTimeout(() => {
          setTradeBtnClicks(0);
        }, WARNING_TIMEOUT);
        btnClicks += 1;
      } else {
        showTradeRuleMsg(intl);
      }
    }
    if (group === UNIT) {
      chartUnit = value;
    }
    if (group === SOURCE) {
      chartSource = value;
      if (chartUnit === UNIT_CURRENCY) {
        chartUnit = UNIT_ENERGY;
      }
    }
    setSource(chartSource);
    setUnit(chartUnit);
    setTradeBtnClicks(btnClicks);
  };

  const handleTimespanUpdate = (inputStart, inputFinish) => {
    // Validate inputs
    if (!inputStart || !inputFinish || !inputStart.isValid || !inputFinish.isValid) {
      logger.error('Invalid date inputs to handleTimespanUpdate:', { inputStart, inputFinish });
      return;
    }

    try {
      // Set the start to the start of the day.
      const updatedStart = inputStart.setZone(timezone).startOf('day');
      // Set the finish to the end of the day.
      const updatedFinish = inputFinish.setZone(timezone).endOf('day');

      // Check if this is the same as the current datespan to avoid unnecessary updates
      const isSameStart = updatedStart.toMillis() === datespan.start.toMillis();
      const isSameFinish = updatedFinish.toMillis() === datespan.finish.toMillis();

      if (isSameStart && isSameFinish) {
        logger.log('Timespan update skipped - dates are identical');
        return;
      }

      // Check if it's a reasonable date range (not too large)
      const spanDays = updatedFinish.diff(updatedStart, 'days').days;
      if (spanDays > 60) {
        logger.warn(`Date range is very large (${spanDays} days). This might cause performance issues.`);
        FlashesStore.flash(
          FlashesStore.WARNING,
          `Selected date range is large (${Math.round(spanDays)} days). Consider selecting a smaller range for better performance.`,
        );
      }

      logger.log(
        'Timespan update - from user interaction:',
        updatedStart.toISO(),
        'to',
        updatedFinish.toISO(),
      );

      // Update the state with the new date range
      setDatespan({ start: updatedStart, finish: updatedFinish });
      setProgressBarLabel(preparingToApplyNewDateRange);
    } catch (error) {
      logger.error('Error updating timespan:', error);
    }
  };

  /**
   * Closes the loading modal and resets all loading states.
   * Shows "New date range applied" message briefly before closing.
   */
  const closeAndResetModal = () => {
    setProgressBarLabel(newDateRangeApplied);
    setTimeout(() => {
      setIsModalOpen(false);
      setLoadingChunks(false);
      setIsInitializing(true);
      setIsFullyLoaded(false);
    }, MODAL_CLOSE_DELAY);
  };
  // Only build main data if we have a portfolio
  const mainData = useMemo(() => {
    const defaultData = {
      portfolio: { propertyMembers: {}, userMembers: {} },
      buy: { rules: {}, data: {} },
      sell: { rules: {}, data: {} },
      meters: {},
      properties: {},
      users: {},
    };

    if (!combinedData) {
      return defaultData;
    }

    try {
      const { portfolio: portfolioData } = combinedData;

      if (!portfolioData) {
        closeAndResetModal();
        return defaultData;
      }

      // Check trade data in portfolioData before building
      if (Array.isArray(portfolioData.tradeSetSummary)) {
        logger.log(`Building main data with ${portfolioData.tradeSetSummary.length} trade summaries`);

        // Check the timestamps in the trade data
        const timeRanges = new Set();
        portfolioData?.tradeSetSummary?.forEach((trade) => {
          trade?.data?.forEach((d) => {
            if (d?.range?.finish) {
              timeRanges.add(d.range.finish);
            }
          });
        });

        logger.log(`Trade data spans ${timeRanges.size} unique time points: ${[...timeRanges].slice(0, 10).join(', ')}${timeRanges.size > 10 ? '...' : ''}`);
      } else {
        logger.warn('No trade summary data in portfolio!');
      }

      // Build the main data with the full portfolio
      const result = buildMainData(portfolioData, portfolioTimezone);

      // Log key metrics from the result
      const buyDataPoints = Object.keys(result?.buy?.data).length;
      const sellDataPoints = Object.keys(result?.sell?.data).length;

      logger.log(`Built main data with ${buyDataPoints} buy data points and ${sellDataPoints} sell data points`);
      closeAndResetModal();
      return result;
    } catch (error) {
      logger.error('Error building main data:', error);
      // Provide a safe fallback
      closeAndResetModal();
      return defaultData;
    }
  }, [combinedData, portfolioTimezone]);
  const { propertyMembers, userMembers } = mainData?.portfolio || {};

  const dataLoading = isPending || loadingChunks;

  const renderPortfolioControls = (portfolioData) => {
    const { start: startTime, finish: finishTime } = datespan;
    const locale = getDefaultLocale();
    const updatedStartTime = startTime.setZone(timezone).setLocale(locale);
    const updatedFinishTime = finishTime.setZone(timezone).setLocale(locale);

    // Large portfolio property size
    const largePortfolioPropertyCount = APIConfig().LARGE_PORTFOLIO_PROPERTY_COUNT || 50;

    // Count the number of properties in this portfolio
    const portfolioPropertyCount = Object.keys(propertyMembers || {}).length || 0;
    // Set isLargePortfolio flag to true when property count exceeds
    // the configured large portfolio property count
    const isLargePortfolio = portfolioPropertyCount >= largePortfolioPropertyCount;

    return (
      <DashboardControl
        chartView={chartView}
        chartViewSelector={
          (
            dropdownOpen,
            toggleFunc,
            chartViewData,
            setChartViewFunc,
            dataSource,
          ) => chartViewSelector(
            dropdownOpen,
            toggleFunc,
            chartViewData,
            setChartViewFunc,
            dataSource,
            isLargePortfolio,
          )
        }
        controlSetStateFunc={(opts) => controlSetStateFunc(opts)}
        downloadSelectorOpen={downloadSelectorOpen}
        mainData={portfolioData}
        minStart={active?.start}
        timespan={{ start: updatedStartTime, finish: updatedFinishTime }}
        timezone={timezone}
        setChartViewFunc={setChartView}
        source={source}
        timespanUpdateFunc={handleTimespanUpdate}
        toggleFunc={toggle}
        viewSelectorOpen={viewSelectorOpen}
        unit={unit}
        isModalOpen={isModalOpen}
      />
    );
  };

  // Calculate whether we have valid data
  const hasData = !!combinedData;

  // Only calculate these if we have data
  const propertyCount = hasData ? (Object.keys(propertyMembers)?.length || 0) : 0;
  const userCount = hasData ? (Object.keys(userMembers)?.length || 0) : 0;
  const { start: finalStart, finish: finalFinish } = datespan || {};

  // Create header inputs only if we have real data
  const headerInputs = {
    uuid: uuid || '',
    propertyCount,
    timespan: {
      start: finalStart,
      finish: finalFinish,
    },
    userCount,
  };

  // Create the loading message
  const periodsMultiplier = aggregation === P1D ? 1 : 2;
  // To show the request initialisation we are doing a +1 on the chunksLoaded
  const periodsLoaded = (chunksLoaded + 1) * periodsMultiplier;
  const periodsTotal = chunksTotal * periodsMultiplier;
  const daysLabel = intl.formatMessage({
    id: 'portfolio.portfolio_show.applying_new_time_range.overlay.days.label',
    defaultMessage: 'days',
  });
  const hourlyLabel = intl.formatMessage({
    id: 'portfolio.portfolio_show.applying_new_time_range.overlay.hours.label',
    defaultMessage: 'hours',
  });
  const periodsType = aggregation === P1D ? daysLabel : hourlyLabel;
  const loadingMessage = intl.formatMessage({
    id: 'portfolio.portfolio_show.applying_new_time_range.overlay.label',
    defaultMessage: 'Applying new time range... (loading {periodsLoaded} of {periodsTotal} {periodsType})',
  }, {
    periodsLoaded,
    periodsTotal,
    periodsType,
  });

  // Log the loading state for debugging
  logger.log(`Rendering with loading state: loadingChunks=${loadingChunks}, chunksLoaded=${chunksLoaded}, chunksTotal=${chunksTotal}, message="${loadingMessage}"`);

  // Loading overlay props
  // Detailed description of determining the loading state (message and progress value)
  // can be found in https://enosi.atlassian.net/browse/PT-1750
  const finalChunksLoaded = isfullyLoaded ? (chunksLoaded + 2) : (chunksLoaded + 1);
  const progressBarValue = isInitializing ? 0 : ((finalChunksLoaded) / (chunksTotal + 2)) * 100;

  const progressLabel = isInitializing || isfullyLoaded
    ? progressBarLabel
    : intl.formatMessage({
      id: 'portfolio.portfolio_show.applying_new_date_range.overlay.progress_label',
      defaultMessage: 'Loading {periodsLoaded} of {periodsTotal} {periodsType}',
    }, {
      periodsLoaded: chunksLoaded + 1,
      periodsTotal: chunksTotal,
      periodsType,
    });
  const loadingOverlayProps = {
    bodyLabel: intl.formatMessage({
      id: 'portfolio.portfolio_show.applying_new_date_range.overlay.body_label',
      defaultMessage: 'Updating to show {dateRange}.',
    }, { dateRange: buildLocalizedDateRangeLabel(intl, finalStart, finalFinish, timezone) }),
    headerLabel: (
      <FormattedMessage
        id="portfolio.portfolio_show.applying_new_date_range.overlay.header_label"
        defaultMessage="{calenderIcon} Applying new date range"
        values={{
          calenderIcon: <FontAwesomeIcon icon={faCalendar} />,
        }}
      />
    ),
    progressBarValue,
    periodsLoaded,
    periodsTotal,
    progressLabel,
    updateProgress: setIsModalOpen,
  };

  const pageTitle = intl.formatMessage({ id: 'portfolio.portfolio_show.page.title', defaultMessage: 'Enosi - Portfolio: {portfolioTitle}' }, { portfolioTitle: title });
  const breadcrumbTitle = intl.formatMessage({ id: 'portfolio.portfolio_show.breadcrumbs.portfolio.label', defaultMessage: 'Portfolios' });
  const overviewTitle = intl.formatMessage({ id: 'portfolio.portfolio_show.heading.overview.title', defaultMessage: 'Overview' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      {/* Show loading overlay when loading or transitioning */}
      {(dataLoading) && (
        <LoadingOverlay inputs={loadingOverlayProps} />
      )}

      {/* Basic page structure that's always shown */}
      <Breadcrumbs breadcrumbs={[{ name: breadcrumbTitle, path: '/portfolios' }, { name: title }]} />

      {/* Show content only once we have data */}
      {hasData && (
        <>
          <Heading
            title={title}
            subtitle={subHeading(headerInputs)}
            context={overviewTitle}
            controls={renderPortfolioControls(mainData)}
          />
          <PortfolioShowTradeEnergy
            chartView={chartView}
            controlSetStateFunc={controlSetStateFunc}
            handleTimespanUpdate={handleTimespanUpdate}
            historianAggregation={aggregation}
            mainData={mainData}
            timespan={dateRangeToTimeRange({
              start: datespan.start,
              finish: datespan.finish,
            }, timezone)}
            timezone={timezone}
            tradeAggregation={aggregation}
            unit={unit}
            source={source}
            selectedKeys={selectedKeys}
            setSelectedKeys={setSelectedKeys}
          />
        </>
      )}
    </>
  );
}

export default PortfolioShow;

PortfolioShow.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
      uuid: PropTypes.string,
    }),
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
    }),
  }).isRequired,
  relay: PropTypes.shape({
    environment: PropTypes.shape({}),
  }),
  queryParams: PropTypes.shape({
    timezone: PropTypes.string,
    aggregateBy: PropTypes.string,
    groupBy: PropTypes.string,
    xAxisDomain: PropTypes.string,
    source: PropTypes.string,
    unit: PropTypes.string,
  }).isRequired,
  initialView: PropTypes.shape({
    aggregateBy: PropTypes.string,
    groupBy: PropTypes.string,
    xAxisDomain: PropTypes.string,
  }).isRequired,
};

PortfolioShow.defaultProps = {
  relay: undefined,
};
